import type { SemanticMatch } from '../domain/types'
import { withSemanticMatches } from './analyze'
import { conservativePreviewAssessment } from './conservativePreview'
import {
  applyOutcomeLensPolicy,
  withAssessmentTier,
  type OutcomeLensResult,
} from './outcomeLenses'
import {
  applySemanticConfirmationPolicy,
  semanticConfirmationSignalReason,
  type SemanticConfirmationResult,
} from './semanticConfirmation'
import type { SemanticRouteDecision } from './semanticRouting'

export type SemanticOutcomePolicyInput = {
  prompt: string
  baseOutcomeLenses: OutcomeLensResult
  semanticDecision: SemanticRouteDecision
  semanticMatches: SemanticMatch[]
}

export type SemanticOutcomePolicyResult = {
  confirmation: SemanticConfirmationResult
  outcomeLenses: OutcomeLensResult
}

/**
 * Composes the semantic confirmation gate with all three result lenses.
 *
 * No lens may expose a tier below the confirmed capability floor. This keeps
 * Direct unavailable unless deterministic Best fit and semantic routing both
 * select Direct.
 */
export function applySemanticOutcomePolicy({
  prompt,
  baseOutcomeLenses,
  semanticDecision,
  semanticMatches,
}: SemanticOutcomePolicyInput): SemanticOutcomePolicyResult {
  const previewAssessment = conservativePreviewAssessment(
    prompt,
    baseOutcomeLenses.bestFit.assessment,
  )
  const confirmation = applySemanticConfirmationPolicy({
    deterministicTier:
      baseOutcomeLenses.bestFit.assessment.tier,
    conservativePreviewTier:
      previewAssessment.tier === 'deep'
        ? 'deep'
        : 'standard',
    semanticDecision,
  })
  const hybridBestFit = withSemanticMatches(
    baseOutcomeLenses.bestFit.assessment,
    semanticMatches,
  )
  const confirmedBestFit = withAssessmentTier(
    hybridBestFit,
    confirmation.tier,
    semanticConfirmationSignalReason(confirmation),
  )

  return {
    confirmation,
    outcomeLenses: applyOutcomeLensPolicy(
      baseOutcomeLenses.bestFit.workItem,
      baseOutcomeLenses.inferredContext,
      confirmedBestFit,
      {
        minimumFastestTier: confirmation.tier,
      },
    ),
  }
}
